import { useEffect, useMemo, useState } from 'react'
import {
  BookOpen, CloudDownload, Database, GitCompareArrows, Receipt, Scale, Search, Table2, TrendingDown, TrendingUp, Users, Wallet,
} from 'lucide-react'
import { api } from '../lib/api'
import { laadKubus } from '../lib/kubus'
import { useLaad } from '../lib/laden'
import { Link, navigeer, useTitel } from '../lib/router'
import { bvKort, useGekozenBvs, useStaat, zet } from '../lib/staat'
import { aantal, bedrag } from '../lib/velden'
import type { Kubus } from '../../gedeeld/types'
import { Bedrag, Blok, KiesBvs, Tegel } from './stukjes'
import { Tabel } from './Tabel'
import { Foutvlak, Laden } from './ui'

/* ===========================================================================
 *  Het overzicht
 *
 *  Waar je begint: zoeken, de cijfers van dit jaar voor de bv's die je
 *  bekijkt, hoe het archief ervoor staat, en de weg naar elk scherm.
 *
 *  Omzet en kosten volgen de indeling van de groep (dezelfde als in de
 *  rapportage van het dashboard). Een rekening die niet is ingedeeld telt
 *  wel mee in het resultaat, maar niet in omzet of kosten -- en dat staat er
 *  dan bij.
 * =========================================================================== */

interface Cijfers { omzet: number; kosten: number; resultaat: number; oningedeeld: number }

function cijfersVan(k: Kubus, bvs: string[]): Map<string, Cijfers> {
  const uit = new Map<string, Cijfers>()
  const soortVan = new Map(k.indeling.groepen.map((g) => [g.id, g.soort]))
  for (const c of k.cellen) {
    if (!bvs.includes(c[0]) || c[2] < 1) continue
    const groep = k.indeling.plek[`${c[0]}|${c[1]}`]
    const soort = groep ? soortVan.get(groep) : undefined
    const balans = k.schema[`${c[0]}|${c[1]}`]?.balans
    const isWv = balans ? balans === 'W' : soort ? soort === 'omzet' || soort === 'kosten' : !/^[0-3]/.test(c[1])
    if (!isWv) continue
    const o = uit.get(c[0]) ?? { omzet: 0, kosten: 0, resultaat: 0, oningedeeld: 0 }
    o.resultaat -= c[3]
    if (soort === 'omzet') o.omzet -= c[3]
    else if (soort === 'kosten') o.kosten += c[3]
    else o.oningedeeld += c[3]
    uit.set(c[0], o)
  }
  return uit
}

export default function Start() {
  const ik = useStaat((s) => s.ik)
  const catalogus = useStaat((s) => s.catalogus)
  const bvs = useGekozenBvs()
  useTitel('Overzicht', 'Alles uit Exact')

  const jaren = useLaad(() => api.jaren(bvs), [bvs.join(',')])
  const jaar = jaren.data?.saldi[0] ?? new Date().getFullYear()
  const [kubus, setKubus] = useState<Kubus | null>(null)
  const [fout, setFout] = useState<string | null>(null)

  useEffect(() => {
    if (!jaren.data) return
    let weg = false
    setFout(null)
    laadKubus(bvs, jaar, 'saldi').then((k) => { if (!weg) setKubus(k) }).catch((e) => { if (!weg) setFout(e instanceof Error ? e.message : String(e)) })
    return () => { weg = true }
  }, [bvs.join(','), jaar, !!jaren.data]) // eslint-disable-line react-hooks/exhaustive-deps

  const stand = useLaad(() => api.stand(bvs), [bvs.join(',')])

  const perBv = useMemo(() => (kubus ? cijfersVan(kubus, bvs) : null), [kubus, bvs])
  const samen = useMemo(() => {
    if (!perBv) return null
    const s = { omzet: 0, kosten: 0, resultaat: 0, oningedeeld: 0 }
    for (const c of perBv.values()) { s.omzet += c.omzet; s.kosten += c.kosten; s.resultaat += c.resultaat; s.oningedeeld += c.oningedeeld }
    return s
  }, [perBv])

  const archief = useMemo(() => {
    const r = stand.data ?? []
    return {
      rijen: r.reduce((a, x) => a + (x.aantal ?? 0), 0),
      afgekapt: r.filter((x) => x.afgekapt).length,
      fouten: r.filter((x) => x.laatste_fout).length,
      laatst: Math.max(0, ...r.map((x) => x.laatst_at ?? 0)),
    }
  }, [stand.data])

  const voornaam = ik?.naam.split(' ')[0] ?? ''

  return (
    <div className="pagina">
      <div className="start-kop">
        <div>
          <h1>Hallo {voornaam}</h1>
          <p className="stil">Alles uit Exact, tot op de regel. {bvs.length === 1 ? bvKort(bvs[0]) : `${bvs.length} bv's`} in beeld.</p>
        </div>
        <span className="alleen-smal"><KiesBvs /></span>
      </div>

      <button type="button" className="zoek-groot" onClick={() => zet({ zoekOpen: true })}>
        <Search size={18} />
        <span>Zoek een boekstuk, factuurnummer, omschrijving of relatie…</span>
        <kbd>Ctrl K</kbd>
      </button>

      {fout && <Foutvlak fout={fout} />}
      <div className="tegels">
        <Tegel label={`Omzet ${jaar}`} getal={samen ? bedrag(samen.omzet) : '…'} icoon={<TrendingUp size={18} />} naar={`/grootboek?jaar=${jaar}&rijen=groep`}
          sub="volgens de indeling van de groep" />
        <Tegel label={`Kosten ${jaar}`} getal={samen ? bedrag(samen.kosten) : '…'} icoon={<TrendingDown size={18} />} naar={`/grootboek?jaar=${jaar}&rijen=groep`} />
        <Tegel label={`Resultaat ${jaar}`} getal={samen ? bedrag(samen.resultaat) : '…'} icoon={<Wallet size={18} />} toon={samen && samen.resultaat < 0 ? 'gevaar' : undefined}
          naar={`/vergelijken?kol=${encodeURIComponent(JSON.stringify([{ j: jaar - 1, v: 1, t: 12 }, { j: jaar, v: 1, t: 12 }]))}`}
          sub={samen && Math.abs(samen.oningedeeld) > 0.5 ? `waarvan ${bedrag(-samen.oningedeeld)} niet ingedeeld` : 'winst positief'} />
        <Tegel label="Archief" getal={stand.data ? aantal(archief.rijen) : '…'} icoon={<Database size={18} />} naar="/archief"
          toon={archief.fouten ? 'warn' : undefined}
          sub={stand.data ? `${archief.afgekapt} afgekapt · ${archief.fouten} met fout` : undefined} />
      </div>

      {perBv && perBv.size > 1 && (
        <Blok titel={`Per bv, ${jaar}`} icoon={<Scale size={15} />}
          rechts={<Link naar={`/vergelijken?kol=${encodeURIComponent(JSON.stringify(bvs.slice(0, 24).map((b) => ({ j: jaar, v: 1, t: 12, b: [b] }))))}`}>naast elkaar vergelijken</Link>}>
          <Tabel
            klein
            rijen={[...perBv.entries()].sort((a, b) => b[1].omzet - a[1].omzet)}
            sleutel={([b]) => b}
            onKlik={([b]) => navigeer(`/grootboek?jaar=${jaar}&rijen=groep&bv=${b}`)}
            kolommen={[
              { sleutel: 'b', label: 'Bv', cel: ([b]) => bvKort(b) },
              { sleutel: 'o', label: 'Omzet', rechts: true, cel: ([, c]) => <Bedrag n={c.omzet} />, voet: <Bedrag n={samen?.omzet} /> },
              { sleutel: 'k', label: 'Kosten', rechts: true, cel: ([, c]) => <Bedrag n={c.kosten} />, voet: <Bedrag n={samen?.kosten} /> },
              { sleutel: 'r', label: 'Resultaat', rechts: true, cel: ([, c]) => <strong><Bedrag n={c.resultaat} /></strong>, voet: <Bedrag n={samen?.resultaat} /> },
              { sleutel: 'm', label: 'Marge', rechts: true, cel: ([, c]) => (c.omzet ? `${((c.resultaat / c.omzet) * 100).toFixed(1)}%` : '') },
            ]}
          />
        </Blok>
      )}
      {!kubus && !fout && <Laden tekst="De cijfers van dit jaar…" />}

      <h2 className="sectie-kop">Waar wil je heen?</h2>
      <div className="snel-tegels">
        <SnelTegel naar="/grootboek" icoon={<BookOpen size={18} />} titel="Grootboek" tekst="Saldi per rekening, groep of kostenplaats; per bv of per periode." />
        <SnelTegel naar="/vergelijken" icoon={<GitCompareArrows size={18} />} titel="Vergelijken" tekst="Jaar tegen jaar, maanden, kwartalen, bv's naast elkaar." />
        <SnelTegel naar="/v/boekingen" icoon={<Table2 size={18} />} titel="Boekingsregels" tekst="Elke regel van elke boeking, met filters en een draaitabel." />
        <SnelTegel naar="/relaties" icoon={<Users size={18} />} titel="Relaties" tekst="Leveranciers en klanten, met wat er openstaat." />
        <SnelTegel naar="/aansluiting" icoon={<Scale size={18} />} titel="Aansluiting" tekst="Kloppen de saldi van Exact met de regels in het archief?" />
        <SnelTegel naar="/live" icoon={<CloudDownload size={18} />} titel="Live uit Exact" tekst="Een vraag rechtstreeks aan Exact, voor wat het archief niet heeft." />
      </div>

      <h2 className="sectie-kop"><Receipt size={16} /> Alles in het archief</h2>
      <div className="soort-tegels">
        {catalogus.map((s) => {
          const n = (stand.data ?? []).filter((r) => r.soort === s.sleutel).reduce((a, r) => a + (r.aantal ?? 0), 0)
          return (
            <Link key={s.sleutel} naar={`/v/${s.sleutel}`} className="soort-tegel">
              <strong>{s.naam}</strong>
              <span className="stil">{s.uitleg}</span>
              <span className="soort-aantal">{stand.data ? aantal(n) : ''}</span>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

function SnelTegel({ naar, icoon, titel, tekst }: { naar: string; icoon: React.ReactNode; titel: string; tekst: string }) {
  return (
    <Link naar={naar} className="snel-tegel">
      <span className="snel-icoon">{icoon}</span>
      <span><strong>{titel}</strong><span className="stil">{tekst}</span></span>
    </Link>
  )
}
